


































































































































































































































































































































































































































































































































































































import { mailMergeService } from '@/services'
import { FormState, MailMergeTemplate, TemplateDataSource } from '@/types/MailMergeTypes'
import { getAxiosErrors } from '@/utils'
import saveAs from 'file-saver'
import Vue from 'vue'

export default Vue.extend({
    name: 'MailMergeTemplateDialog',

    props: {
        /** v-model: controls dialog visibility */
        value: { type: Boolean, default: false },
        /** Pass an existing template to enable edit mode. */
        template: { type: Object as () => MailMergeTemplate | null, default: null },
        /** Available data-source fields for variable reference / insertion. */
        dataSourceField: { type: Object as () => TemplateDataSource },
        /** Optional direct URL to the current file for downloading. */
        downloadUrl: { type: String as () => string | null, default: null },
        /** URL of the external editor application (iframe src). */
        editorUrl: { type: String },
    },

    data() {
        return {
            loading: false,
            downloadLoading: false,
            parseLoading: false,
            success: false,
            snackbar: false,
            variableSearch: '',
            isFullscreen: false,
            /** Bumped to force-remount the editor iframe (resets it to its initial "No document loaded" state). */
            editorKey: 0,
            /** Resolves with the edited ArrayBuffer when EDITOR_READY_TO_SUBMIT arrives. */
            pendingSubmit: null as ((buf: ArrayBuffer) => void) | null,
            form: {
                name: '',
                mode: 'raw',
                file: null,
            } as FormState,
            errors: {} as Record<string, string>,
        }
    },

    computed: {
        dialogOpen: {
            get(): boolean { return this.value },
            set(val: boolean) { this.$emit('input', val) },
        },
        isUpdate(): boolean {
            return !!this.template && !!this.template.id
        },
        filteredDataSourceColumns(): TemplateDataSource {
            if (!this.variableSearch) return this.dataSourceField as TemplateDataSource
            const q = this.variableSearch.toLowerCase()
            return {
                ...this.dataSourceField,
                columns: this.dataSourceField.columns.filter(
                    col =>
                        col.name.toLowerCase().includes(q) ||
                        (col.displayName || col.name).toLowerCase().includes(q),
                ),
            }
        },
        formDisabled(): boolean {
            return this.loading || this.parseLoading || (this.success && !this.isUpdate)
        },
        /** Derived origin for postMessage targeting. */
        editorOrigin(): string {
            try { return new URL(this.editorUrl).origin } catch { return '*' }
        },
    },

    watch: {
        value(opened: boolean) {
            if (opened) {
                window.addEventListener('message', this.handleEditorMessage)
                this.initForm()
            } else {
                window.removeEventListener('message', this.handleEditorMessage)
            }
        },
        template: {
            async handler(template: MailMergeTemplate | null) {
                if (!this.value) {
                    return
                }

                if (!template) {
                    // Parent cleared the selected template while the dialog is mounted —
                    // reset the form, drop any loaded file, and remount the iframe so we
                    // don't carry stale state into the next open.
                    this.form.name = ''
                    this.form.mode = 'raw'
                    this.form.file = null
                    this.errors = {}
                    this.success = false
                    this.resetEditor()
                    return
                }

                try {
                    const response = await mailMergeService.downloadTemplate(template.id ?? '')
                    // 1. Get Content-Disposition header (Axios keys are lowercase)
                    const contentDisposition = response.headers['content-disposition']
                    let filename = 'default_template.docx' // Fallback name
                    // 2. Parse filename using Regex
                    if (contentDisposition) {
                        
                        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
                        if (filenameMatch && filenameMatch[1]) {
                            // Remove quotes around the extracted filename
                            filename = filenameMatch[1].replace(/['"]/g, '')
                        }
                    }

                    // 3. Convert Blob to File Object
                    this.form.file = new File([response.data], filename, { type: response.data.type })

                    this.onEditFileChange(this.form.file)
                } catch (e) {
                    this.errors = {
                        ...this.errors,
                        General: 'Failed to load the existing template file. You can still edit the metadata, or close and try again.',
                    }
                }
            },
        },
    },

    beforeDestroy() {
        window.removeEventListener('message', this.handleEditorMessage)
    },

    methods: {
        // ── Form lifecycle ───────────────────────────────────────────────────

        initForm() {
            this.success = false
            this.errors = {}
            this.variableSearch = ''
            this.isFullscreen = false
            this.pendingSubmit = null

            if (this.template) {
                this.form.name = this.template.name ?? ''
                this.form.mode = this.template.mode ?? 'raw'
                this.form.file = null
            } else {
                this.form.name = ''
                this.form.mode = 'raw'
                this.form.file = null
            }
        },

        setMode(mode: 'raw' | 'edit') {
            if (this.formDisabled) return
            this.form.mode = mode
            this.form.file = null
            this.isFullscreen = false
            this.pendingSubmit = null
            this.clearError('Content')
            this.clearError('File')
        },

        // ── iframe communication ─────────────────────────────────────────────

        sendToEditor(message: Record<string, unknown>) {
            const frame = this.$refs.editorFrame as HTMLIFrameElement | undefined
            frame?.contentWindow?.postMessage(message, this.editorOrigin)
        },

        handleEditorMessage(event: MessageEvent) {
            if (event.origin !== this.editorOrigin) return
            if (event.data?.type === 'EDITOR_READY_TO_SUBMIT' && event.data.payload) {
                this.pendingSubmit?.(event.data.payload as ArrayBuffer)
                this.pendingSubmit = null
            }
        },

        /** Resets the embedded editor to its empty "No document loaded" state by remounting the iframe. */
        resetEditor() {
            this.pendingSubmit = null
            this.editorKey++
        },

        /** Sends REQUEST_SUBMIT and waits for EDITOR_READY_TO_SUBMIT (5 s timeout). */
        requestEditorFile(): Promise<ArrayBuffer> {
            return new Promise<ArrayBuffer>((resolve, reject) => {
                const timeout = setTimeout(() => {
                    this.pendingSubmit = null
                    reject(new Error('Editor did not respond within 5 seconds.'))
                }, 5_000)

                this.pendingSubmit = (buf: ArrayBuffer) => {
                    clearTimeout(timeout)
                    resolve(buf)
                }

                this.sendToEditor({ type: 'REQUEST_SUBMIT' })
            })
        },

        // ── File handling ────────────────────────────────────────────────────

        onRawFileChange() {
            this.clearError('File')
        },

        async onEditFileChange(file: File | null) {
            this.clearError('File')
            if (!file) {
                // The bundled editor's FILE_LOADED handler ignores a null/empty buffer,
                // so remount the iframe to return it to its initial empty state.
                this.resetEditor()
                return
            }

            this.parseLoading = true
            try {
                const arrayBuffer = await file.arrayBuffer()
                this.sendToEditor({
                    type: 'FILE_LOADED',
                    payload: {
                        incomingBuffer: arrayBuffer,
                        mailMergeVariables: this.dataSourceField,
                    },
                })
            } catch {
                this.errors = { ...this.errors, File: 'Could not read the selected file.' }
            } finally {
                this.parseLoading = false
            }
        },

        // ── Submit ───────────────────────────────────────────────────────────

        async onSubmit() {
            this.loading = true
            this.errors = {}

            const payload = new FormData()
            payload.append('name', this.form.name.trim())
            payload.append('mode', this.form.mode)
            payload.append('pageName', this.dataSourceField.pageName || 'Unknown')

            if (this.template?.id != null) {
                payload.append('id', String(this.template.id))
            }

            if (this.form.mode === 'raw') {
                if (this.form.file) {
                    payload.append('file', this.form.file)
                }
            } else {
                // edit mode: request edited file from the iframe editor
                try {
                    const buffer = await this.requestEditorFile()
                    const fileName = this.form.file?.name ?? 'template.docx'
                    const file = new File([buffer], fileName, {
                        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                    })
                    payload.append('file', file)
                } catch {
                    this.errors = { General: 'The editor did not return a file. Please try again.' }
                    this.loading = false
                    return
                }
            }

            try {
                await mailMergeService.upsertTemplate(payload)
                if (!this.isUpdate) {
                    this.resetEditor()
                }
                this.success = true
            } catch (e: any) {
                this.errors = getAxiosErrors(e)
            } finally {
                this.loading = false
            }
        },

        // ── Utilities ────────────────────────────────────────────────────────

        async copyToClipboard(text: string) {
            try {
                await navigator.clipboard.writeText(text)
                this.snackbar = true
            } catch { /* unavailable */ }
        },

        async onDownload() {
            this.downloadLoading = true
            delete this.errors['Download']
            try {
                const result = await mailMergeService.downloadTemplate(this.template?.id ?? '')
                // Handle the result as needed
                saveAs(result.data, this.template?.fileName || 'template.docx')
            } catch (error) {
                // Handle the error as needed
                this.errors = { ...this.errors, Download: 'Failed to download the template. Please try again.' }   
            } finally {
                this.downloadLoading = false
            }
        },

        clearError(field: string) {
            if (this.errors[field]) {
                const next = { ...this.errors }
                delete next[field]
                this.errors = next
            }
        },

        onClose() {
            if (this.loading) return
            this.$emit('input', false)
        },
    },
})
